import { PINNING_STEP, stepTitles } from './constants';

import { USER_ROLE } from '~common';
import { checkRolesMatchWithPrimaryUserRole } from '~utils/user.util';

export const getNaturalNextStep = (currentStep: PINNING_STEP): PINNING_STEP => {
  const steps = Object.values(PINNING_STEP);
  const currentIndex = steps.indexOf(currentStep);
  if (currentIndex < steps.length - 1) {
    return steps[currentIndex + 1];
  }
  return currentStep;
};

export const getNaturalPreviousStep = (
  currentStep: PINNING_STEP,
): PINNING_STEP => {
  const steps = Object.values(PINNING_STEP);
  const currentIndex = steps.indexOf(currentStep);
  if (currentIndex > 0) {
    return steps[currentIndex - 1];
  }
  return currentStep;
};

export const getTitleOfCurrentStep = (step: PINNING_STEP): string | null => {
  return stepTitles[step] ?? null;
};

export const getNextStep = (
  currentStep: PINNING_STEP,
  isHavingExistingPlacement?: boolean,
  selectedGooglePlaceId?: string,
  isRelocationFlow?: boolean,
  isPlacementAssignedStation?: boolean,
  isAssigningToExistingPlacement?: boolean,
): PINNING_STEP => {
  // skip distribution channel step for some user roles
  if (
    checkRolesMatchWithPrimaryUserRole([
      USER_ROLE.DISTRIBUTOR,
      USER_ROLE.LIMITED_DISTRIBUTOR,
      USER_ROLE.PARTNER,
    ]) &&
    currentStep ===
      getNaturalPreviousStep(
        PINNING_STEP.ASSIGN_STATION_ADD_DISTRIBUTION_CHANNEL,
      )
  ) {
    return getNaturalNextStep(
      PINNING_STEP.ASSIGN_STATION_ADD_DISTRIBUTION_CHANNEL,
    );
  }

  // Skip Enter venue’s address step
  if (
    selectedGooglePlaceId &&
    selectedGooglePlaceId !== '' &&
    currentStep ===
      getNaturalPreviousStep(PINNING_STEP.CREATE_VENUE_ADD_ADDRESS)
  ) {
    return getNaturalNextStep(PINNING_STEP.CREATE_VENUE_ADD_ADDRESS);
  }

  if (
    currentStep === PINNING_STEP.CONFIRM_VENUE &&
    !isHavingExistingPlacement
  ) {
    return PINNING_STEP.ASSIGN_STATION_SCAN_QR;
  }

  if (
    currentStep === PINNING_STEP.ASSIGN_STATION_SELECT_STATION &&
    isAssigningToExistingPlacement &&
    !isPlacementAssignedStation
  ) {
    return PINNING_STEP.ASSIGN_STATION_PIN_COORDINATES;
  }

  if (
    currentStep === PINNING_STEP.ASSIGN_STATION_SELECT_STATION &&
    isPlacementAssignedStation
  ) {
    return PINNING_STEP.CONFIRM_SWAP_STATION;
  }

  if (currentStep === PINNING_STEP.CONFIRM_SWAP_STATION)
    return PINNING_STEP.ASSIGN_STATION_UPLOAD_PHOTOS;

  // Relocation flow: from select action screen (swap goes to scan QR for new station)
  if (currentStep === PINNING_STEP.ASSIGN_STATION_SELECT_ACTION) {
    return isRelocationFlow
      ? PINNING_STEP.CONFIRM_RELOCATION
      : PINNING_STEP.ASSIGN_STATION_SCAN_QR;
  }

  // Relocation flow: after confirm relocation, go to edit placement name
  if (currentStep === PINNING_STEP.CONFIRM_RELOCATION)
    return PINNING_STEP.ASSIGN_STATION_ADD_PLACEMENT_NAME;

  return getNaturalNextStep(currentStep);
};

export const getPreviousStep = (
  currentStep: PINNING_STEP,
  selectedVenueId?: string,
  selectedGooglePlaceId?: string,
  isPlacementAssignedStation?: boolean,
  isRelocationFlow?: boolean,
  isAddingFromReview?: boolean,
  isHavingExistingPlacement?: boolean,
  isAssigningToExistingPlacement?: boolean,
  contactSkippedStep?: PINNING_STEP,
): PINNING_STEP => {
  switch (currentStep) {
    case PINNING_STEP.CREATE_VENUE_ADD_NAME:
      return PINNING_STEP.START;

    case PINNING_STEP.ASSIGN_STATION_SCAN_QR:
      if (selectedVenueId) {
        if (isPlacementAssignedStation) {
          return PINNING_STEP.ASSIGN_STATION_SELECT_ACTION;
        }
        if (isHavingExistingPlacement) {
          return PINNING_STEP.ASSIGN_STATION_SELECT_EXISTING_PLACEMENT;
        }
        return PINNING_STEP.CONFIRM_VENUE;
      }
      // A skipped venue contact goes back to the screen where Skip was pressed
      if (contactSkippedStep) return contactSkippedStep;
      break;

    case PINNING_STEP.ASSIGN_STATION_SELECT_EXISTING_PLACEMENT:
      if (isAddingFromReview) return PINNING_STEP.REVIEW_CONFIRM;
      return PINNING_STEP.CONFIRM_VENUE;

    case PINNING_STEP.ASSIGN_STATION_SELECT_ACTION:
      return PINNING_STEP.ASSIGN_STATION_SELECT_EXISTING_PLACEMENT;

    case PINNING_STEP.CONFIRM_SWAP_STATION:
      return PINNING_STEP.ASSIGN_STATION_SELECT_STATION;

    case PINNING_STEP.CONFIRM_RELOCATION:
      return PINNING_STEP.ASSIGN_STATION_SELECT_ACTION;

    case PINNING_STEP.ASSIGN_STATION_ADD_PLACEMENT_NAME:
      if (isRelocationFlow || isPlacementAssignedStation)
        return PINNING_STEP.CONFIRM_RELOCATION;
      break;

    case PINNING_STEP.VENUE_DETAIL_REVIEW:
    case PINNING_STEP.PLACEMENT_DETAIL_REVIEW:
      return PINNING_STEP.REVIEW_CONFIRM;
    case PINNING_STEP.ASSIGN_STATION_PIN_COORDINATES:
      if (isAssigningToExistingPlacement)
        return PINNING_STEP.ASSIGN_STATION_SELECT_STATION;
      break;

    case PINNING_STEP.ASSIGN_STATION_UPLOAD_PHOTOS:
      if (isRelocationFlow) return PINNING_STEP.ASSIGN_STATION_PIN_COORDINATES;
      if (isPlacementAssignedStation) return PINNING_STEP.CONFIRM_SWAP_STATION;
      break;
    default:
      break;
  }

  if (
    currentStep ===
    getNaturalNextStep(PINNING_STEP.ASSIGN_STATION_ADD_DISTRIBUTION_CHANNEL)
  ) {
    // skip distribution channel step for some user roles
    if (
      checkRolesMatchWithPrimaryUserRole([
        USER_ROLE.DISTRIBUTOR,
        USER_ROLE.LIMITED_DISTRIBUTOR,
        USER_ROLE.PARTNER,
      ])
    ) {
      return getNaturalPreviousStep(
        PINNING_STEP.ASSIGN_STATION_ADD_DISTRIBUTION_CHANNEL,
      );
    }
  }

  // Skip Enter venue’s address step
  if (
    selectedGooglePlaceId &&
    selectedGooglePlaceId !== '' &&
    currentStep === getNaturalNextStep(PINNING_STEP.CREATE_VENUE_ADD_ADDRESS)
  ) {
    return getNaturalPreviousStep(PINNING_STEP.CREATE_VENUE_ADD_ADDRESS);
  }

  return getNaturalPreviousStep(currentStep);
};
